import React, { useState } from 'react'
import { useLanguage } from '../context/LanguageContext'

const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"

export default function TopCommunitiesSection() {
  const { t } = useLanguage()
  const [activeTab, setActiveTab] = useState('roi')

  const tabs = [
    { id: 'roi', label: t('topCommunities.tabs.roi') },
    { id: 'villas', label: t('topCommunities.tabs.villas') },
    { id: 'budget', label: t('topCommunities.tabs.budget') },
    { id: 'strategy', label: t('topCommunities.tabs.strategy') },
  ]

  // Extract translation data
  const roiData = t('topCommunities.roi') || {}
  const villasData = t('topCommunities.villas') || {}
  const budgetData = t('topCommunities.budget') || {}
  const strategyData = t('topCommunities.strategy') || {}

  return (
    <div style={{ backgroundColor: '#f9fbfd', width: '100%', padding: '80px 0' }}>
      <style>{`
        .tc-container {
          max-width: 1300px;
          margin: 0 auto;
          padding: 0 20px;
          box-sizing: border-box;
        }
        .tc-heading-row {
          display: flex;
          flex-direction: row;
          gap: 15px;
          align-items: flex-end;
          flex-wrap: wrap;
        }
        .tc-heading-desc {
          flex: 0 1 400px;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
        }
        .tc-tabs-wrapper {
          display: flex;
          gap: 12px;
          margin-bottom: 40px;
          overflow-x: auto;
          -ms-overflow-style: none;
          scrollbar-width: none;
          padding-bottom: 10px;
        }
        .tc-tabs-wrapper::-webkit-scrollbar {
          display: none;
        }
        .tc-tab-btn {
          background: #fff;
          border: 1.5px solid rgba(0, 46, 83, 0.1);
          color: #5a6577;
          font-family: ${FONT};
          font-size: 15px;
          font-weight: 600;
          padding: 12px 24px;
          border-radius: 50px;
          cursor: pointer;
          white-space: nowrap;
          transition: all 0.3s ease;
        }
        .tc-tab-btn:hover {
          border-color: rgba(197, 160, 89, 0.5);
          color: ${NAVY};
        }
        .tc-tab-btn.active {
          background: ${NAVY};
          color: #fff;
          border-color: ${NAVY};
          box-shadow: 0 8px 20px rgba(0, 46, 83, 0.2);
        }

        /* Content Animations */
        .tc-content-pane {
          animation: fadeUp 0.4s ease-out forwards;
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Generic Card Styling */
        .tc-card {
          background: #fff;
          border-radius: 16px;
          padding: 32px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
          border: 1px solid rgba(0, 46, 83, 0.05);
          height: 100%;
        }

        /* Lists */
        .tc-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 12px;
        }
        .tc-list-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-family: ${FONT};
          font-size: 14px;
          color: ${NAVY};
          font-weight: 500;
          background: rgba(197, 160, 89, 0.05);
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid rgba(197, 160, 89, 0.15);
        }
        .tc-list-item::before {
          content: '✓';
          color: ${GOLD};
          font-weight: bold;
        }

        /* Insights Box */
        .tc-insight-box {
          background: linear-gradient(135deg, ${NAVY} 0%, #001f3a 100%);
          border-radius: 12px;
          padding: 24px;
          margin-top: 32px;
          color: #fff;
          position: relative;
          overflow: hidden;
        }
        .tc-insight-box::after {
          content: '';
          position: absolute;
          top: -50px;
          right: -50px;
          width: 150px;
          height: 150px;
          background: rgba(197, 160, 89, 0.1);
          border-radius: 50%;
        }

        /* Budget Grid */
        .tc-budget-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
        }

        /* Strategy Grid */
        .tc-strategy-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .tc-budget-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 768px) {
          .tc-budget-grid { grid-template-columns: 1fr; }
          .tc-strategy-grid { grid-template-columns: 1fr; }
          .tc-heading-row { flex-direction: column; align-items: flex-start; gap: 12px; }
          .tc-heading-desc { flex: 1 1 auto; }
        }
      `}</style>

      <div className="tc-container">
        {/* Header */}
        <div className="tc-heading-row" style={{ marginBottom: '40px' }}>
          <div style={{ flex: '1 1 auto' }}>
            <div style={{ marginBottom: '8px' }}>
              <span style={{
                backgroundColor: 'rgba(197,160,89,0.1)',
                color: GOLD,
                fontFamily: FONT,
                fontSize: '12px',
                fontWeight: 700,
                borderRadius: '5px',
                padding: '5px 8px',
                display: 'inline-block',
              }}>
                {t('topCommunities.badge')}
              </span>
            </div>
            <h2 style={{
              fontFamily: FONT,
              fontSize: 'clamp(23px, 3.5vw, 44px)',
              fontWeight: 600,
              lineHeight: '1.2em',
              letterSpacing: '-0.3px',
              color: NAVY,
              margin: 0,
              maxWidth: '550px',
            }}>
              {t('topCommunities.heading')}
            </h2>
          </div>
          <div className="tc-heading-desc">
            <p style={{
              fontFamily: FONT,
              fontSize: '16px',
              fontWeight: 400,
              lineHeight: '1.6em',
              color: NAVY,
              margin: 0,
            }}>
              {t('topCommunities.desc')}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="tc-tabs-wrapper">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tc-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content Panes */}
        <div style={{ position: 'relative', minHeight: '300px' }}>

          {/* 1. ROI Tab */}
          {activeTab === 'roi' && (
            <div className="tc-content-pane">
              <div className="tc-card">
                <h3 style={{ fontFamily: FONT, fontSize: '22px', color: NAVY, margin: '0 0 8px' }}>{roiData.title}</h3>
                <p style={{ fontFamily: FONT, fontSize: '15px', color: '#5a6577', margin: '0 0 30px' }}>{roiData.desc}</p>
                
                <ul className="tc-list">
                  {roiData.list && roiData.list.map((item, idx) => (
                    <li key={idx} className="tc-list-item">{item}</li>
                  ))}
                </ul>

                <div className="tc-insight-box">
                  <h4 style={{ fontFamily: FONT, fontSize: '14px', color: GOLD, textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 12px' }}>{roiData.insightTitle}</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {roiData.insights && roiData.insights.map((insight, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <span style={{ color: GOLD }}>✦</span>
                        <p style={{ margin: 0, fontFamily: FONT, fontSize: '15px', lineHeight: 1.5 }}>{insight}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 2. Villas Tab */}
          {activeTab === 'villas' && (
            <div className="tc-content-pane">
              <div className="tc-card">
                <h3 style={{ fontFamily: FONT, fontSize: '22px', color: NAVY, margin: '0 0 8px' }}>{villasData.title}</h3>
                <p style={{ fontFamily: FONT, fontSize: '15px', color: '#5a6577', margin: '0 0 30px' }}>{villasData.desc}</p>
                
                <ul className="tc-list">
                  {villasData.list && villasData.list.map((item, idx) => (
                    <li key={idx} className="tc-list-item">{item}</li>
                  ))}
                </ul>

                <div className="tc-insight-box">
                  <h4 style={{ fontFamily: FONT, fontSize: '14px', color: GOLD, textTransform: 'uppercase', letterSpacing: '1px', margin: '0 0 12px' }}>{villasData.insightTitle}</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {villasData.insights && villasData.insights.map((insight, idx) => (
                      <div key={idx} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <span style={{ color: GOLD }}>✦</span>
                        <p style={{ margin: 0, fontFamily: FONT, fontSize: '15px', lineHeight: 1.5 }}>{insight}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 3. Budget Tab */}
          {activeTab === 'budget' && (
            <div className="tc-content-pane tc-budget-grid">
              {budgetData.categories && budgetData.categories.map((cat, idx) => (
                <div key={idx} className="tc-card" style={{ display: 'flex', flexDirection: 'column' }}>
                  <div style={{ marginBottom: '20px' }}>
                    <span style={{
                      display: 'inline-block',
                      fontFamily: FONT, fontSize: '11px', fontWeight: 700,
                      backgroundColor: NAVY, color: '#fff',
                      padding: '4px 10px', borderRadius: '4px', textTransform: 'uppercase',
                      marginBottom: '10px'
                    }}>
                      {cat.name}
                    </span>
                    <h4 style={{ fontFamily: FONT, fontSize: '20px', color: GOLD, margin: 0 }}>{cat.range}</h4>
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <p style={{ fontFamily: FONT, fontSize: '13px', color: '#5a6577', fontWeight: 600, marginBottom: '8px' }}>Communities:</p>
                    <ul style={{ paddingLeft: '18px', margin: '0 0 20px', color: NAVY, fontFamily: FONT, fontSize: '14px', lineHeight: 1.6 }}>
                      {cat.areas.map((area, aIdx) => (
                        <li key={aIdx}>{area}</li>
                      ))}
                    </ul>
                  </div>

                  <div style={{
                    marginTop: 'auto',
                    backgroundColor: 'rgba(197, 160, 89, 0.08)',
                    padding: '12px', borderRadius: '8px',
                    borderLeft: `3px solid ${GOLD}`
                  }}>
                    <p style={{ margin: 0, fontFamily: FONT, fontSize: '12px', color: NAVY }}>
                      <strong>Best for:</strong> {cat.bestFor}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 4. Strategy Guide Tab */}
          {activeTab === 'strategy' && (
            <div className="tc-content-pane">
              <div className="tc-card">
                <h3 style={{ fontFamily: FONT, fontSize: '22px', color: NAVY, margin: '0 0 24px', textAlign: 'center' }}>{strategyData.title}</h3>
                <div className="tc-strategy-grid">
                  {strategyData.guide && strategyData.guide.map((item, idx) => (
                    <div key={idx} style={{
                      display: 'flex', alignItems: 'center', gap: '20px',
                      padding: '24px',
                      backgroundColor: '#fff',
                      border: '1.5px solid rgba(0,46,83,0.06)',
                      borderRadius: '12px',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.02)'
                    }}>
                      <div style={{ textAlign: 'right', flex: 1 }}>
                        <p style={{ fontFamily: FONT, fontSize: '13px', color: '#5a6577', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>If your goal is</p>
                        <h4 style={{ fontFamily: FONT, fontSize: '18px', color: NAVY, margin: 0, fontWeight: 700 }}>{item.goal}</h4>
                      </div>
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'rgba(197, 160, 89, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: GOLD, flexShrink: 0 }}>
                        <span style={{ fontSize: '18px' }}>➔</span>
                      </div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontFamily: FONT, fontSize: '13px', color: '#5a6577', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Consider</p>
                        <h4 style={{ fontFamily: FONT, fontSize: '16px', color: GOLD, margin: 0, fontWeight: 600 }}>{item.action}</h4>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}
